// PayDeeporbUSDC.jsx - Refactored for subscription payments with invoice tracking
import React, { useState } from "react";
import {
  createWalletClient,
  createPublicClient,
  custom,
  http,
  parseUnits,
} from "viem";
import { mainnet, arbitrum, base, optimism } from "viem/chains";
import { useWallet } from '../hooks/useWallet';
import { useInstanceStore } from '../stores/UseInstanceStore';
import { ethers } from "ethers";

// USDC_CONTRACT=0xaf88d065e77c8cc2239327c5edb3a432268e5831 #verifed
// PAYMENT_CONTRACT_ADDRESS is loaded from config.paymentContractAddress per environment

// USDC addresses by chain
const USDC_BY_CHAIN = {
  1: "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48",      // Ethereum
  42161: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831", // Arbitrum One
  8453: "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913",  // Base
  10: "0x0b2c639c533813f4aa9d7837caf62653d097ff85",    // Optimism
};

const viemNetworks = {
  1: mainnet,
  42161: arbitrum,
  8453: base,
  10: optimism,
};

// Minimal ABIs
const ERC20_ABI = [
  { type: "function", name: "approve", stateMutability: "nonpayable", inputs: [{ name: "spender", type: "address" }, { name: "amount", type: "uint256" }], outputs: [{ type: "bool" }] },
  { type: "function", name: "allowance", stateMutability: "view", inputs: [{ name: "owner", type: "address" }, { name: "spender", type: "address" }], outputs: [{ type: "uint256" }] },
  { type: "function", name: "decimals", stateMutability: "view", inputs: [], outputs: [{ type: "uint8" }] },
  { type: "function", name: "balanceOf", stateMutability: "view", inputs: [{ name: "account", type: "address" }], outputs: [{ type: "uint256" }] },
];

const PAYMENT_ABI = [
  {
    type: "function",
    name: "pay",
    stateMutability: "nonpayable",
    inputs: [
      { name: "invoiceId", type: "bytes32" },
      { name: "token", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [],
  },
];

/**
 * PayDeeporbUSDC - USDC payment component with subscription integration
 * 
 * @param {Object} props
 * @param {string} props.mode - "upgrade" or "extend"
 * @param {string} props.amount - USDC amount (e.g., "49" for $49)
 * @param {string} props.planTier - Target plan tier for upgrades (e.g., "TRADER", "PRO")
 * @param {string} props.billingPeriod - "MONTHLY" or "ANNUAL"
 * @param {boolean} props.prorate - Whether to apply prorated credit (upgrade only)
 * @param {string} props.buttonText - Custom button text
 * @param {Object} props.config - Configuration object with apiBaseUrl
 * @param {Function} props.onSuccess - Callback after successful subscription update
 * @param {Function} props.onError - Callback on error
 */
export default function PayDeeporbUSDC({
  mode = "extend",  // "upgrade" or "extend"
  amount,
  planTier,  // Required for upgrade mode
  billingPeriod = "MONTHLY",  // "MONTHLY" or "ANNUAL"
  prorate = true,  // Apply prorated credit for upgrades
  buttonText,
  config,  // Optional config override
  onSuccess,
  onError,
}) {
  const { account, network, jwtToken } = useWallet();
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  
  const CONTRACT_ADDRESS = config?.paymentContractAddress;

  // Get API base URL from store (primary source) or config prop (override)
  const storeApiBaseUrl = useInstanceStore((state) => state.instance.apiBaseUrl);
  const apiBaseUrl = config?.apiBaseUrl 
    || storeApiBaseUrl 
    || import.meta.env.VITE_API_BASE_URL 
    || "http://localhost:8000";

  // Validation
  if (!account) return <div className="text-purple-300">Connect wallet to subscribe.</div>;
  if (!network) return <div className="text-gray-500">Detecting network...</div>;
  if (!USDC_BY_CHAIN[network.chainId]) {
    return <div className="text-red-500">Unsupported network. Please switch to Ethereum, Arbitrum, Base, or Optimism.</div>;
  }
  if (!amount) {
    return <div className="text-red-500">Amount is required</div>;
  }
  if (mode === "upgrade" && !planTier) {
    return <div className="text-red-500">Plan tier is required for upgrades</div>;
  }

  // Generate unique invoice ID
  const generateInvoiceId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    return `katbot-${mode}-${timestamp}-${random}`;
  };

  // Call backend API after successful payment
  const notifyBackend = async (invoiceId, transactionHash) => {
    try {
      // Use JWT token from wallet context
      if (!jwtToken) {
        throw new Error("Not authenticated. Please log in.");
      }

      const endpoint = mode === "upgrade" 
        ? `${apiBaseUrl}/payment/subscription/upgrade`
        : `${apiBaseUrl}/payment/subscription/extend`;

      const payload = mode === "upgrade"
        ? {
            plan_tier: planTier,
            billing_period: billingPeriod,
            prorate: prorate,
            invoice_id: invoiceId,
            tx_hash: transactionHash,
          }
        : {
            billing_period: billingPeriod,
            invoice_id: invoiceId,
            tx_hash: transactionHash
          };

      console.log("Notifying backend:", { endpoint, payload });
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${jwtToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "API request failed");
      }

      const result = await response.json();
      console.log("Subscription API response:", result);
      return result;
    } catch (error) {
      console.error("Backend notification error:", error);
      throw error;
    }
  };

  const onClick = async () => {
    if (loading) return;
    if (!CONTRACT_ADDRESS) {
      setStatus("Configuration error: payment contract address not set.");
      console.error("paymentContractAddress is not set in config");
      return;
    }

    setLoading(true);
    setStatus("");

    try {
      const chainId = network?.chainId;
      console.log("Payment initiated:", { mode, planTier, billingPeriod, amount, chainId });

      if (!window.ethereum) {
        throw new Error("Please install MetaMask.");
      }

      const usdc = USDC_BY_CHAIN[chainId];
      if (!usdc) {
        throw new Error(`Unsupported chain (chainId=${chainId}). Switch to Ethereum, Arbitrum, Base, or Optimism.`);
      }

      // Generate invoice ID before payment
      const invoiceId = generateInvoiceId();
      console.log("Generated invoice ID:", invoiceId);

      // Setup clients
      const chain = viemNetworks[chainId] || mainnet;
      setStatus("Connecting wallet…");
      
      const walletClient = createWalletClient({
        chain,
        transport: custom(window.ethereum),
      });
      
      const publicClient = createPublicClient({
        chain,
        transport: http(),
      });

      // Get USDC decimals
      setStatus("Preparing payment…");
      let decimals = 6n;
      try {
        const dec = await publicClient.readContract({
          address: usdc,
          abi: ERC20_ABI,
          functionName: "decimals",
        });
        decimals = BigInt(dec);
      } catch (_) {
        decimals = 6n;
      }

      const amountParsed = parseUnits(String(amount), Number(decimals));

      // Check user's USDC balance
      const balance = await publicClient.readContract({
        address: usdc,
        abi: ERC20_ABI,
        functionName: "balanceOf",
        args: [account],
      });

      console.log("Payment details:", {
        amount: String(amount),
        amountParsed: amountParsed.toString(),
        decimals: Number(decimals),
        userBalance: balance.toString(),
        usdc,
        contract: CONTRACT_ADDRESS,
      });

      if (balance < amountParsed) {
        throw new Error(`Insufficient USDC balance. You have ${Number(balance) / 10**Number(decimals)} USDC but need ${amount} USDC`);
      }

      // Check and approve USDC if needed
      let allowance = await publicClient.readContract({
        address: usdc,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [account, CONTRACT_ADDRESS],
      });

      console.log("Current allowance:", allowance.toString());

      if (allowance < amountParsed) {
        setStatus(`Approving ${amount} USDC…`);
        const approveHash = await walletClient.writeContract({
          account,
          address: usdc,
          abi: ERC20_ABI,
          functionName: "approve",
          args: [CONTRACT_ADDRESS, amountParsed],
        });
        await publicClient.waitForTransactionReceipt({ hash: approveHash });
        
        // Verify approval went through
        allowance = await publicClient.readContract({
          address: usdc,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [account, CONTRACT_ADDRESS],
        });
        console.log("Allowance after approval:", allowance.toString());
        
        if (allowance < amountParsed) {
          throw new Error("Approval failed - allowance not updated");
        }
      }

      // Convert invoice ID to bytes32 using keccak256 hash
      const invoiceIdBytes32 = ethers.utils.keccak256(
        ethers.utils.toUtf8Bytes(invoiceId)
      );

      console.log("Invoice ID:", invoiceId);
      console.log("Invoice ID (bytes32):", invoiceIdBytes32);
      console.log("Pay transaction args:", {
        invoiceId: invoiceIdBytes32,
        token: usdc,
        amount: amountParsed.toString(),
        contractAddress: CONTRACT_ADDRESS,
      });

      // Submit payment transaction
      setStatus(`Sending ${amount} USDC payment…`);
      const payHash = await walletClient.writeContract({
        account,
        address: CONTRACT_ADDRESS,
        abi: PAYMENT_ABI,
        functionName: "pay",
        args: [invoiceIdBytes32, usdc, amountParsed],
      });

      setStatus("Confirming transaction…");
      const receipt = await publicClient.waitForTransactionReceipt({ hash: payHash });
      
      console.log("Payment confirmed:", receipt.transactionHash);

      // Notify backend of successful payment
      setStatus("Updating subscription…");
      const apiResult = await notifyBackend(invoiceId, receipt.transactionHash);

      // Success!
      const successMessage = mode === "upgrade"
        ? `Successfully upgraded to ${planTier}!`
        : `Successfully extended subscription!`;
      
      setStatus(`✅ ${successMessage}`);
      console.log("Subscription updated:", apiResult);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess({
          transactionHash: receipt.transactionHash,
          invoiceId,
          subscription: apiResult,
        });
      }

    } catch (err) {
      console.error("Payment error:", err);
      const errorMessage = err?.shortMessage || err?.message || "Payment failed";
      setStatus(`❌ ${errorMessage}`);
      
      // Call error callback if provided
      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate button text
  const defaultButtonText = mode === "upgrade"
    ? `Upgrade to ${planTier} - ${amount} USDC`
    : `Extend Subscription - ${amount} USDC`;
  
  const displayText = buttonText || defaultButtonText;

  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={onClick}
        className="px-6 py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        disabled={loading}
        title={`Pay ${amount} USDC for ${mode === "upgrade" ? "upgrade" : "extension"}`}
      >
        {loading ? "Processing..." : displayText}
      </button>
      
      {status && (
        <div className={`text-sm px-3 py-2 rounded ${
          status.startsWith("✅") 
            ? "bg-green-100 text-green-800" 
            : status.startsWith("❌")
            ? "bg-red-100 text-red-800"
            : "bg-blue-100 text-blue-800"
        }`}>
          {status}
        </div>
      )}
    </div>
  );
}
